/**
 * Secure Error Boundary Component
 * Displays user-friendly messages without exposing internal details
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Paper, Button, Typography, Alert } from '@mui/material';
import { Error as ErrorIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { logger } from "../utils/logger";
import { getCapturedConsoleLogs } from '../utils/consoleCapture';
import {
  isChunkLoadError,
  markChunkAutoReloadAttempted,
  shouldAttemptChunkAutoReload,
} from '../utils/chunkLoadErrorUtils';
import { buildErrorFingerprint } from '../../shared/utils/errorCapture/fingerprint';
import {
  logClientErrorSync,
  redactPageUrl,
  redactStackTrace,
  scheduleAutoBoundaryBugReport,
} from '../utils/reportClientIncident';
import { IT_TEAM_NOTIFIED_DETAIL } from '../../shared/utils/errorCapture/itTeamNotifiedCopy';

/** html2canvas is heavy and can throw or stall on large admin trees; tickets still get logs + stack. */
export function shouldSkipErrorBoundaryScreenshot(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }
  const host = window.location.hostname.toLowerCase();
  if (host.startsWith('backoffice.') || host.split('.')[0] === 'backoffice') {
    return true;
  }
  const path = window.location.pathname;
  if (path === '/backoffice' || path.startsWith('/backoffice/')) {
    return true;
  }
  return false;
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode | ((error: Error | null) => ReactNode);
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  errorId: string | null;
  error: Error | null;
  /** True after a successful auto Service Ticket report (IT notified). */
  itTeamNotified: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  private hasReportedError = false;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorId: null, error: null, itTeamNotified: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Generate unique error ID for tracking
    const errorId = `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const isChunkLoadingError = isChunkLoadError(error);

    // Debug logging to console
    logger.error('🔴 ErrorBoundary caught error:', error, {
      errorId: errorId,
      isChunkLoadingError: isChunkLoadingError,
      message: error?.message,
      name: error?.name,
      stack: error?.stack?.substring(0, 300)
    } as any);

    // Log error securely without exposing sensitive information
    logger.error('Error Boundary caught error', error, {
      errorId,
      isChunkLoadingError,
      // Remove sensitive data from error logging
      // message and stack are logged by logger.error() but not exposed in UI
      timestamp: new Date().toISOString()
      // Removed: userAgent, url - these contain sensitive user information
    });

    return { hasError: true, errorId, error, itTeamNotified: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (typeof window !== 'undefined') {
      const pathname = window.location.pathname || '';
      const errorId = this.state.errorId || `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const fingerprint = buildErrorFingerprint({
        errorMessage: error.message || error.name || 'Unknown client error',
        pathname,
        stack: error.stack,
      });
      logClientErrorSync({
        error_type: 'client_error',
        error_message: `${error.message || 'Unknown client error'} [Reference ID: ${errorId}]`,
        stack_trace: redactStackTrace(error.stack ?? undefined) ?? undefined,
        component_stack: redactStackTrace(errorInfo.componentStack ?? undefined) ?? undefined,
        severity: 'medium',
        request_url: redactPageUrl(window.location.href),
        component: 'ErrorBoundary',
        fingerprint,
        correlation_id: errorId,
      });
    }

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // One automatic full reload after deploy/cache skew (dynamic import failure); avoids useless "Try Again" state reset
    if (isChunkLoadError(error) && typeof window !== 'undefined') {
      if (shouldAttemptChunkAutoReload()) {
        markChunkAutoReloadAttempted();
        window.location.reload();
        return;
      }
    }

    if (this.hasReportedError) {
      return;
    }
    this.hasReportedError = true;

    // Create an automatic support ticket for unexpected production errors (once per boundary instance).
    // Defer so React can paint the fallback first; html2canvas is heavy and uses document.write internally.
    const errorId = this.state.errorId || `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const isChunk = isChunkLoadError(error);
    let sentryEventId: string | undefined;
    if (typeof window !== 'undefined' && (window as any).Sentry) {
      const id = (window as any).Sentry.captureException(error, {
        contexts: {
          error_boundary: {
            errorId: this.state.errorId,
            componentStack: errorInfo.componentStack
          }
        }
      });
      if (typeof id === 'string' && id.length > 0) {
        sentryEventId = id;
      }
    }

    const scheduled = scheduleAutoBoundaryBugReport(error, {
      errorId: this.state.errorId,
      isChunk,
      suppressUserAck: true,
      onReported: () => {
        // Only claim IT was notified after a successful bug-report response.
        if (this.state.hasError) {
          this.setState({ itTeamNotified: true });
        }
      },
      buildPayload: async () => {
        const title = `Unhandled UI error: ${error.message || 'Unknown error'}`;
        const description = [
          `Reference ID: ${errorId}`,
          `Message: ${error.message || 'Unknown error'}`,
          `Name: ${error.name || 'Error'}`,
          error.stack ? `Stack: ${error.stack}` : 'Stack: not available',
          errorInfo.componentStack ? `Component stack: ${errorInfo.componentStack}` : 'Component stack: not available',
          sentryEventId ? `Sentry event: ${sentryEventId}` : '',
        ]
          .filter(Boolean)
          .join('\n');

        const browserInfo = {
          userAgent: window.navigator.userAgent,
          platform: window.navigator.platform,
          language: window.navigator.language,
        };

        const consoleLogs = getCapturedConsoleLogs(50);
        let screenshot: string | null = null;
        if (!shouldSkipErrorBoundaryScreenshot()) {
          try {
            screenshot = await this.captureScreenshotWithTimeout(12_000);
          } catch {
            screenshot = null;
          }
        }

        const pathname = window.location.pathname || '';
        const fingerprint = buildErrorFingerprint({
          errorMessage: error.message || error.name || 'Unknown',
          pathname,
          stack: error.stack,
        });

        return {
          title,
          description,
          pageUrl: window.location.href,
          browserInfo,
          consoleLogs,
          screenshot,
          pageState: {
            errorId,
            sentry_event_id: sentryEventId,
          },
          fingerprint,
        };
      },
    });
    if (!scheduled) {
      // Policy skipped ticket (DEV / chunk / ignore filters) — no IT message.
      return;
    }
  }

  /**
   * Full-page screenshots via html2canvas are brittle on large admin UIs (backoffice) and trigger
   * document.write inside the library — skip there and rely on stack + console logs.
   */
  private async captureScreenshotWithTimeout(timeoutMs: number): Promise<string | null> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), timeoutMs);
    });
    try {
      const shot = await Promise.race([this.captureScreenshot(), timeoutPromise]);
      return shot;
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  private async captureScreenshot(): Promise<string | null> {
    if (typeof document === 'undefined' || !document.body) {
      return null;
    }

    const canvas = await (await import('html2canvas')).default(document.body, {
      allowTaint: false,
      useCORS: true,
      logging: false,
      scale: Math.min(1, window.devicePixelRatio || 1),
      width: window.innerWidth,
      height: window.innerHeight,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      onclone: (clonedDoc) => {
        // Hide failed external images to avoid blocking capture
        const failedImages = clonedDoc.querySelectorAll('img[src]');
        failedImages.forEach((img) => {
          const htmlImg = img as HTMLImageElement;
          if (!htmlImg.complete || htmlImg.naturalHeight === 0) {
            htmlImg.style.display = 'none';
          }
        });
      }
    });

    return canvas.toDataURL('image/png', 0.8);
  }

  handleRetry = () => {
    this.setState({ hasError: false, errorId: null, error: null, itTeamNotified: false });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI (supports function for error-aware fallbacks).
      // Still surface IT-ack after a successful auto ticket — custom fallbacks
      // previously hid the default Alert and also suppressed the toast.
      if (this.props.fallback) {
        const fallback = typeof this.props.fallback === 'function'
          ? this.props.fallback(this.state.error)
          : this.props.fallback;
        return (
          <Box sx={{ width: '100%' }}>
            {!isChunkLoadError(this.state.error ?? undefined) && this.state.itTeamNotified && (
              <Alert severity="info" sx={{ m: 2, textAlign: 'left' }} role="status">
                {IT_TEAM_NOTIFIED_DETAIL}
              </Alert>
            )}
            {fallback}
          </Box>
        );
      }

      const isChunkLoadingError = isChunkLoadError(this.state.error ?? undefined);

      // Default secure error UI
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
            p: 3
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              maxWidth: 500,
              width: '100%',
              textAlign: 'center'
            }}
          >
            <ErrorIcon
              sx={{
                fontSize: 64,
                color: 'error.main',
                mb: 2
              }}
            />

            <Typography variant="h5" gutterBottom sx={{ color: 'error.main' }}>
              {isChunkLoadingError ? 'Component Failed to Load' : 'Something went wrong'}
            </Typography>

            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              {isChunkLoadingError ? (
                <>
                  The component failed to load. This may be due to a network issue or an outdated cache.
                  <br />
                  <br />
                  Try refreshing the page or clearing your browser cache.
                </>
              ) : (
                <>
                  We encountered an unexpected error.
                </>
              )}
              {this.state.errorId && (
                <span style={{ display: 'block', marginTop: 8, fontSize: '0.875rem' }}>
                  Reference ID: {this.state.errorId}
                </span>
              )}
            </Typography>

            {!isChunkLoadingError && this.state.itTeamNotified && (
              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }} role="status">
                {IT_TEAM_NOTIFIED_DETAIL}
              </Alert>
            )}

            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              <strong>What you can try:</strong>
              <br />• Refresh the page
              <br />• Clear your browser cache
              <br />• Try again in a few minutes
            </Alert>

            {process.env.NODE_ENV === 'development' && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'left' }}>
                <Typography variant="subtitle2" sx={{ color: 'error.main' }}>
                  Debug Info:
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  Error: {this.state.error?.message || 'No error message'}<br/>
                  Stack: {this.state.error?.stack?.substring(0, 200) || 'No stack trace'}...
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              {isChunkLoadingError ? (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleReload}
                >
                  Reload page
                </Button>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={this.handleRetry}
                  >
                    Try Again
                  </Button>
                  <Button variant="contained" onClick={this.handleReload}>
                    Reload Page
                  </Button>
                </>
              )}
            </Box>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
